import { SUBSCRIPTION_RUNTIME_TYPES, VICKI_MODES, type FrontDeskSettings, type RuntimeProfile, type Workspace } from '@deskworks/shared-types';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AudioLines, Boxes, Building2, ConciergeBell, Cpu, FolderKanban, KeyRound, Mic, Pencil, Plus, Power, Trash2, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { AccountPeople } from '@/components/account-people';
import { EditWorkspaceDialog } from '@/components/edit-workspace-dialog';
import { VickiRhythm } from '@/components/front-desk/vicki-goals';
import { Field } from '@/components/field';
import { McpAccessSection, McpAppsSection } from '@/components/mcp-access';
import { DefinitionList, Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { ConnectSubscriptionButton, invalidateRuntimeProfiles, SubscriptionStatus } from '@/components/runtime-login-dialog';
import { RuntimeProfileDialog } from '@/components/runtime-profile-dialog';
import { RuntimeUsageSummary } from '@/components/runtime-usage';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MobileTabs, MobileTabsSpacer } from '@/components/ui/mobile-tabs';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, errorMessage } from '@/lib/api';
import { formatAbsolute } from '@/lib/format';
import { useApiMutation, useMe, useRuntimeProfiles, useWorkspace, useWorkspaceId } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { RESOURCE_CLASS, RUNTIME_TYPE } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import type { SettingsTab } from '@/router';
import { MODE_TEXT } from '@/routes/front-desk';
import { VickiKnowledge } from '@/routes/vicki-knowledge';
import { WorkerImagesSection } from '@/components/worker-images';

export function SettingsPage() {
  const workspaceId = useWorkspaceId();
  const { tab } = useSearch({ from: '/_authed/w/$workspaceId/settings' });
  const navigate = useNavigate({ from: '/w/$workspaceId/settings' });
  const workspace = useWorkspace(workspaceId);
  const me = useMe();
  const account = me.data?.accounts.find((a) => a.id === workspace.data?.accountId);

  return (
    <Page>
      <PageHeader title={t('Settings')} description={t('What holds for this workspace, and what holds for the whole account.')} />
      <Tabs value={tab ?? 'workspace'} onValueChange={(v) => void navigate({ search: { tab: v as SettingsTab }, replace: true })}>
        {/* Two groups: the workspace's own settings, and the account's, the same in every workspace. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 max-md:hidden">
          <TabGroup label={t('Workspace')} name={workspace.data?.name}>
            <TabsTrigger value="workspace">{t('General')}</TabsTrigger>
            <TabsTrigger value="runtime-profiles">{t('Runtimes')}</TabsTrigger>
          </TabGroup>
          <TabGroup label={t('Account')} name={account?.name}>
            <TabsTrigger value="account">{t('General')}</TabsTrigger>
            <TabsTrigger value="people">{t('People & guests')}</TabsTrigger>
            <TabsTrigger value="vicki">{t('VICKI')}</TabsTrigger>
            <TabsTrigger value="mcp">{t('API & MCP')}</TabsTrigger>
            <TabsTrigger value="worker-images">{t('Worker images')}</TabsTrigger>
          </TabGroup>
        </div>
        <MobileTabs
          label={t('Settings')}
          tabs={[
            { value: 'workspace', label: t('Workspace'), icon: <FolderKanban /> },
            { value: 'runtime-profiles', label: t('Runtimes'), icon: <Cpu /> },
            { value: 'account', label: t('Account'), icon: <Building2 /> },
            { value: 'people', label: t('People & guests'), icon: <Users /> },
            { value: 'vicki', label: t('VICKI'), icon: <ConciergeBell /> },
            { value: 'mcp', label: t('API & MCP'), icon: <KeyRound /> },
            { value: 'worker-images', label: t('Worker images'), icon: <Boxes /> },
          ]}
        />
        <TabsContent value="workspace">
          <WorkspaceTab workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="runtime-profiles">
          <RuntimeProfilesTab workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="account">
          <div className="grid gap-6">
            <AccountTab workspaceId={workspaceId} />
            <DictationSection workspaceId={workspaceId} />
          </div>
        </TabsContent>
        <TabsContent value="people">
          {account ? <AccountPeople accountId={account.id} admin={account.role === 'owner' || account.role === 'admin'} /> : <LoadingRows rows={3} />}
        </TabsContent>
        <TabsContent value="vicki">
          <VickiTab workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="mcp">
          <div className="grid gap-6">
            <McpAppsSection workspaceId={workspaceId} />
            <McpAccessSection workspaceId={workspaceId} />
          </div>
        </TabsContent>
        <TabsContent value="worker-images">
          {account ? <WorkerImagesSection accountId={account.id} admin={account.role === 'owner' || account.role === 'admin'} /> : <LoadingRows rows={3} />}
        </TabsContent>
        <MobileTabsSpacer />
      </Tabs>
    </Page>
  );
}

function TabGroup({ label, name, children }: { label: string; name?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="max-w-48 truncate text-xs font-medium text-muted-foreground" title={name}>
        {label}
        {name && <span className="font-normal"> · {name}</span>}
      </span>
      <TabsList>{children}</TabsList>
    </div>
  );
}

// ---------------------------------------------------------------- runtime profiles

function RuntimeProfilesTab({ workspaceId }: { workspaceId: string }) {
  const profiles = useRuntimeProfiles(workspaceId);
  const workspace = useWorkspace(workspaceId);
  const me = useMe();
  const accountRole = me.data?.accounts.find((a) => a.id === workspace.data?.accountId)?.role;
  // Account-wide runtimes belong to every workspace: only account admins change them.
  const accountAdmin = accountRole === 'owner' || accountRole === 'admin';
  const toggle = useApiMutation(
    (p: RuntimeProfile) => api.runtimeProfiles.update(p.id, { status: p.status === 'active' ? 'disabled' : 'active' }),
    {
      invalidate: [qk.runtimeProfiles(workspaceId), qk.agents(workspaceId)],
      success: (p) => (p.status === 'active' ? t('Runtime enabled') : t('Runtime disabled')),
    },
  );
  const useAgain = useApiMutation((p: RuntimeProfile) => api.runtimeProfiles.makeAvailable(p.id), {
    invalidate: [qk.runtimeProfiles(workspaceId), qk.agents(workspaceId)],
    success: t('Agents on “Automatic” use this runtime again'),
  });

  return (
    <Section
      icon={<Cpu />}
      title={t('Runtimes')}
      description={t('Connections to AI providers (API key or subscription login). Agents on “Automatic” try them by priority, lowest first, and move on when one is used up or can’t be reached. When none is free, they try again an hour later.')}
      actions={
        <RuntimeProfileDialog workspaceId={workspaceId} canShare={accountAdmin}>
          <Button size="sm">
            <Plus /> {t('New runtime')}
          </Button>
        </RuntimeProfileDialog>
      }
    >
      {profiles.isPending ? (
        <LoadingRows rows={3} />
      ) : profiles.isError ? (
        <ErrorState error={profiles.error} onRetry={() => void profiles.refetch()} />
      ) : profiles.data.length === 0 ? (
        <EmptyState
          compact
          title={t('No runtimes yet')}
          description={t('Add a runtime, e.g. “Anthropic API” with your API key or your ChatGPT/Claude subscription. After that, agents can get to work.')}
        />
      ) : (
        <ul className="-mx-5 -mb-5 divide-y border-t">
          {profiles.data.map((p) => (
            <li key={p.id} className="flex flex-wrap items-start gap-3 px-5 py-3 sm:flex-nowrap">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{p.name}</span>
                  {!p.implemented && <Badge tone="amber">{t('not supported yet')}</Badge>}
                  {p.status === 'disabled' && <Badge tone="muted">{t('Disabled')}</Badge>}
                  {p.workspaceId === null && (
                    <Badge tone="outline" title={accountAdmin ? undefined : t('Shared by the account – only account admins can change it')}>
                      {p.sharedWorkspaceIds ? t('{n} workspaces', { n: p.sharedWorkspaceIds.length }) : t('All workspaces')}
                    </Badge>
                  )}
                  {p.unavailable && (
                    <Badge tone="amber" title={p.unavailable.reason}>
                      {p.unavailable.kind === 'limit'
                        ? p.unavailable.until
                          ? t('Limit reached until {date}', { date: formatAbsolute(p.unavailable.until) })
                          : t('Limit reached')
                        : p.unavailable.kind === 'offline'
                          ? p.unavailable.until
                            ? t('Unreachable – next try {date}', { date: formatAbsolute(p.unavailable.until) })
                            : t('Unreachable')
                          : t('Login or key refused')}
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  <span title={t('Agents on “Automatic” try the lowest number first')}>{t('Priority {n}', { n: p.priority })}</span>
                  <span>{RUNTIME_TYPE[p.runtimeType] ?? p.runtimeType}</span>
                  {p.model && <span className="font-mono">{t('default: {model}', { model: p.model })}</span>}
                  <span>{RESOURCE_CLASS[p.resourceClass]?.label ?? p.resourceClass}</span>
                  {SUBSCRIPTION_RUNTIME_TYPES.includes(p.runtimeType) ? (
                    <SubscriptionStatus profile={p} />
                  ) : p.runtimeType === 'local' ? null : (
                    <span className={cn('inline-flex items-center gap-1', !p.hasCredential && 'text-amber-700 dark:text-amber-400')}>
                      <KeyRound className="size-3" />
                      {p.hasCredential ? t('API key saved') : t('No API key')}
                    </span>
                  )}
                </div>
                <RuntimeUsageSummary profile={p} />
              </div>
              {p.workspaceId === null && !accountAdmin ? null : (
                <div className="flex flex-wrap gap-1 sm:shrink-0">
                  {p.unavailable && (
                    <Button variant="ghost" size="sm" onClick={() => useAgain.mutate(p)} disabled={useAgain.isPending} title={t('Agents on “Automatic” skip this runtime until then')}>
                      {t('Use again')}
                    </Button>
                  )}
                  {SUBSCRIPTION_RUNTIME_TYPES.includes(p.runtimeType) && p.status === 'active' && <ConnectSubscriptionButton profile={p} />}
                  <RuntimeProfileDialog workspaceId={workspaceId} profile={p} canShare={accountAdmin}>
                    <Button variant="ghost" size="sm">
                      <Pencil /> {t('Edit')}
                    </Button>
                  </RuntimeProfileDialog>
                  <Button variant="ghost" size="sm" onClick={() => toggle.mutate(p)} disabled={toggle.isPending}>
                    <Power /> {p.status === 'active' ? t('Disable') : t('Enable')}
                  </Button>
                  <DeleteRuntimeButton profile={p} workspaceId={workspaceId} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- dictation

const BROWSER = 'browser';

/**
 * Which runtime transcribes the microphone in the message fields, for the whole account
 * (docs/features/conversations.md#dictation). Account admins choose; the default is the browser's speech recognition.
 */
function DictationSection({ workspaceId }: { workspaceId: string }) {
  const accountId = useWorkspace(workspaceId).data?.accountId;
  const settings = useQuery({ queryKey: qk.dictation(accountId ?? ''), queryFn: () => api.dictation.settings(accountId!), enabled: !!accountId });
  const save = useApiMutation((id: string | null) => api.dictation.setRuntime(accountId!, id), {
    invalidate: [qk.dictation(accountId ?? '')],
    success: t('Dictation saved'),
  });
  const runtime = settings.data?.runtime ?? null;
  const choices = settings.data?.choices ?? null;
  const scope = (c: NonNullable<typeof choices>[number]) =>
    c.sharedWorkspaces === 'all' ? t('All workspaces') : c.sharedWorkspaces !== null ? t('{n} workspaces', { n: c.sharedWorkspaces }) : (c.workspaceName ?? '');

  return (
    <Section
      icon={<Mic />}
      title={t('Dictation')}
      description={t('How the microphone in the message fields turns speech into text, in every workspace of the account. The default is the browser’s speech recognition (free). With an OpenAI API runtime, recordings are transcribed by OpenAI after you stop, with punctuation and better with technical terms (gpt-4o-transcribe, about 0.6 cents a minute, paid with that runtime’s key).')}
    >
      {settings.isPending ? (
        <LoadingRows rows={1} />
      ) : settings.isError ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : (
        <div className="grid gap-2">
          {choices ? (
            <Select value={runtime?.id ?? BROWSER} onValueChange={(v) => save.mutate(v === BROWSER ? null : v)} disabled={save.isPending}>
              <SelectTrigger className="sm:w-96" aria-label={t('Dictation')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BROWSER}>{t('Browser speech recognition (default)')}</SelectItem>
                {choices.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    {scope(c) && <span className="text-muted-foreground"> · {scope(c)}</span>}
                    {!c.usable && <span className="text-muted-foreground"> · {t('switched off or no key')}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm">
              {runtime ? t('Recordings are transcribed with the runtime “{name}”.', { name: runtime.name }) : t('The browser’s speech recognition.')}{' '}
              <span className="text-muted-foreground">{t('Only account admins change it.')}</span>
            </p>
          )}
          {choices?.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('Add an OpenAI API runtime to have recordings transcribed by OpenAI.')}</p>
          )}
          {runtime && !runtime.usable && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {t('The runtime “{name}” is switched off or has no API key: dictation uses the browser’s speech recognition until then.', { name: runtime.name })}
            </p>
          )}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- Live VICKI

const OFF = 'off';

/**
 * Talking with VICKI in her chats through OpenAI's realtime voice, for the whole account
 * (docs/features/front-desk.md#live-vicki). Off by default; account admins switch it on with an OpenAI API runtime.
 */
function LiveVickiSection({ workspaceId }: { workspaceId: string }) {
  const accountId = useWorkspace(workspaceId).data?.accountId;
  const settings = useQuery({ queryKey: qk.liveVicki(accountId ?? ''), queryFn: () => api.frontDesk.live(accountId!), enabled: !!accountId });
  const save = useApiMutation((id: string | null) => api.frontDesk.setLive(accountId!, id), {
    invalidate: [qk.liveVicki(accountId ?? '')],
    success: (s) => (s.runtime ? t('Live VICKI is on') : t('Live VICKI is off')),
  });
  const runtime = settings.data?.runtime ?? null;
  const choices = settings.data?.choices ?? null;
  const usable = choices?.filter((c) => c.usable) ?? [];

  return (
    <Section
      icon={<AudioLines />}
      title={t('Live VICKI')}
      description={t('Talk with VICKI in her chats as on the phone: she answers right away with her voice, looks things up and acts with her tools while you talk, writes bigger work into the chat for her to work on and tells you when it is done. It runs on OpenAI’s realtime voice and is paid with the key of the OpenAI API runtime you choose (about 5 to 20 cents a minute).')}
    >
      {settings.isPending ? (
        <LoadingRows rows={1} />
      ) : settings.isError ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : (
        <div className="grid gap-2">
          {choices ? (
            <Select value={runtime?.id ?? OFF} onValueChange={(v) => save.mutate(v === OFF ? null : v)} disabled={save.isPending || (!usable.length && !runtime)}>
              <SelectTrigger className="sm:w-96" aria-label={t('Live VICKI')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={OFF}>{t('Off (default)')}</SelectItem>
                {choices.map((c) => (
                  <SelectItem key={c.id} value={c.id} disabled={!c.usable}>
                    {t('On with “{name}”', { name: c.name })}
                    {!c.usable && <span className="text-muted-foreground"> · {t('switched off or no key')}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm">
              {runtime ? t('On, with the runtime “{name}”.', { name: runtime.name }) : t('Off.')}{' '}
              <span className="text-muted-foreground">{t('Only account admins change it.')}</span>
            </p>
          )}
          {choices && !usable.length && (
            <p className="text-xs text-muted-foreground">{t('Live VICKI needs an active OpenAI API runtime with a key (a ChatGPT subscription has none). Add one under Workspace → Runtimes to switch it on.')}</p>
          )}
          {runtime && !runtime.usable && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {t('The runtime “{name}” is switched off or has no API key: Live VICKI is off until then.', { name: runtime.name })}
            </p>
          )}
        </div>
      )}
    </Section>
  );
}

/** Delete a runtime; the backend answers 409 (naming the agents) while it is still in use — shown inside the dialog. */
function DeleteRuntimeButton({ profile, workspaceId }: { profile: RuntimeProfile; workspaceId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.runtimeProfiles.delete(profile.id);
      if (profile.workspaceId === null) invalidateRuntimeProfiles(queryClient);
      void queryClient.invalidateQueries({ queryKey: qk.runtimeProfiles(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: qk.agents(workspaceId) });
      toast.success(t('Runtime deleted'));
      setOpen(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setError(null);
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
          <Trash2 /> {t('Delete')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('Delete runtime “{name}”?', { name: profile.name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('The stored API key / login is removed. Agents must not use this runtime anymore — switch them to another runtime first.')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm break-words text-destructive">
            {error}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
          <Button variant="destructive" loading={busy} onClick={() => void onDelete()}>
            {t('Delete runtime')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------- workspace

function WorkspaceTab({ workspaceId }: { workspaceId: string }) {
  const workspace = useWorkspace(workspaceId);
  const me = useMe();
  if (workspace.isPending) return <LoadingRows rows={3} />;
  if (workspace.isError) return <ErrorState error={workspace.error} onRetry={() => void workspace.refetch()} />;
  const ws = workspace.data;
  const account = me.data?.accounts.find((a) => a.id === ws.accountId);
  const canEdit = account?.role === 'owner' || account?.role === 'admin';
  return (
    <Card className="p-5">
      {canEdit && (
        <div className="mb-4 flex justify-end">
          <EditWorkspaceDialog workspace={ws} accountMaxWorkers={account?.maxActiveWorkers}>
            <Button variant="outline" size="sm">
              <Pencil /> {t('Edit')}
            </Button>
          </EditWorkspaceDialog>
        </div>
      )}
      <DefinitionList
        items={[
          { label: t('Name'), value: ws.name },
          { label: t('Short name'), value: <code className="font-mono text-xs">{ws.slug}</code> },
          { label: t('Description'), value: ws.description || <span className="text-muted-foreground">—</span> },
          { label: t('Account'), value: account ? t('{name} (your role: {role})', { name: account.name, role: roleLabel(account.role) }) : ws.accountId },
          {
            label: t('Max. concurrent workers'),
            value: (
              <>
                {ws.maxActiveWorkers}
                <span className="ml-2 text-xs text-muted-foreground">{t('This many agents can work in this workspace at the same time.')}</span>
              </>
            ),
          },
          { label: t('Created'), value: <RelativeTime value={ws.createdAt} /> },
          { label: t('Workspace ID'), value: <code className="font-mono text-xs">{ws.id}</code> },
        ]}
      />
      {ws.systemRole === 'account' ? (
        <p className="mt-6 border-t pt-4 text-sm text-muted-foreground">
          {t('This is the account’s own workspace: the Front Desk with VICKI lives here and everyone in the account works in it. It stays as long as the account.')}
        </p>
      ) : canEdit && (
        <div className="mt-6 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium">{t('Delete workspace')}</p>
            <p className="text-sm text-muted-foreground">{t('Removes the workspace with all its desks, agents, connections and Work Items.')}</p>
          </div>
          <DeleteWorkspaceButton workspace={ws} />
        </div>
      )}
    </Card>
  );
}

/** Delete the workspace with everything in it (account owners/admins); the name must be typed to confirm. */
function DeleteWorkspaceButton({ workspace }: { workspace: Workspace }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const overview = useQuery({ queryKey: qk.overview(workspace.id), queryFn: () => api.workspaces.overview(workspace.id) });
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const counts = overview.data?.counts;

  const onDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.workspaces.delete(workspace.id);
      queryClient.removeQueries({ queryKey: qk.ws(workspace.id) });
      void queryClient.invalidateQueries({ queryKey: qk.accountWorkspaces(workspace.accountId) });
      void queryClient.invalidateQueries({ queryKey: qk.accountDesks(workspace.accountId) });
      void queryClient.invalidateQueries({ queryKey: qk.me });
      toast.success(t('Workspace “{name}” deleted', { name: workspace.name }));
      setOpen(false);
      void navigate({ to: '/' });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setTyped('');
          setError(null);
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
          <Trash2 /> {t('Delete workspace')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('Delete workspace “{name}”?', { name: workspace.name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {counts
              ? t(
                  'Its {desks}, {agents} and {connections} are deleted, together with every Work Item, conversation, skill, view and the workspace database. Running workers are stopped. This cannot be undone.',
                  {
                    desks: counts.desks === 1 ? t('1 desk') : t('{n} desks', { n: counts.desks }),
                    agents: counts.agents === 1 ? t('1 agent') : t('{n} agents', { n: counts.agents }),
                    connections: counts.connections === 1 ? t('1 connection') : t('{n} connections', { n: counts.connections }),
                  },
                )
              : t('All its desks, agents and connections are deleted, together with every Work Item, conversation, skill, view and the workspace database. Running workers are stopped. This cannot be undone.')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Field label={t('Type “{name}” to confirm', { name: workspace.name })}>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
        </Field>
        {error && (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm break-words text-destructive">
            {error}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
          <Button variant="destructive" loading={busy} disabled={typed.trim() !== workspace.name} onClick={() => void onDelete()}>
            {t('Delete workspace')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------- account

/** The account itself: the same in each of its workspaces. */
function AccountTab({ workspaceId }: { workspaceId: string }) {
  const accountId = useWorkspace(workspaceId).data?.accountId;
  const me = useMe();
  const workspaces = useQuery({ queryKey: qk.accountWorkspaces(accountId ?? ''), queryFn: () => api.accounts.workspaces(accountId!), enabled: !!accountId });
  const account = me.data?.accounts.find((a) => a.id === accountId);
  if (!account) return <LoadingRows rows={3} />;
  return (
    <>
      <Card className="p-5">
        <DefinitionList
          items={[
            { label: t('Name'), value: account.name },
            { label: t('Short name'), value: <code className="font-mono text-xs">{account.slug}</code> },
            { label: t('Your role'), value: roleLabel(account.role) },
            {
              label: t('Max. concurrent workers'),
              value: (
                <>
                  {account.maxActiveWorkers}
                  <span className="ml-2 text-xs text-muted-foreground">{t('This many agents can work in all workspaces of the account together.')}</span>
                </>
              ),
            },
            { label: t('Created'), value: <RelativeTime value={account.createdAt} /> },
            { label: t('Account ID'), value: <code className="font-mono text-xs">{account.id}</code> },
          ]}
        />
      </Card>
      <Section icon={<FolderKanban />} title={t('Workspaces')} description={t('The workspaces of the account. Each has its own desks, agents, connections and settings.')}>
        {workspaces.isPending ? (
          <LoadingRows rows={2} />
        ) : workspaces.isError ? (
          <ErrorState error={workspaces.error} onRetry={() => void workspaces.refetch()} />
        ) : (
          <ul className="-mx-5 -mb-5 divide-y border-t">
            {workspaces.data.map((w) => (
              <li key={w.id}>
                <Link to="/w/$workspaceId/settings" params={{ workspaceId: w.id }} search={{ tab: 'workspace' }} className="flex flex-wrap items-center gap-2 px-5 py-3 text-sm hover:bg-muted/50">
                  <span className="font-medium">{w.name}</span>
                  {w.systemRole === 'account' && <Badge tone="outline">{t('Account’s own')}</Badge>}
                  {w.id === workspaceId && <Badge tone="muted">{t('This workspace')}</Badge>}
                  {w.description && <span className="min-w-0 basis-full truncate text-xs text-muted-foreground">{w.description}</span>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}

// ---------------------------------------------------------------- VICKI

/** VICKI works for the whole account: how much she does on her own, her rhythm, her voice and what she remembers. */
function VickiTab({ workspaceId }: { workspaceId: string }) {
  const accountId = useWorkspace(workspaceId).data?.accountId;
  if (!accountId) return <LoadingRows rows={3} />;
  return (
    <div className="grid grid-cols-1 gap-6">
      <VickiModeSection accountId={accountId} />
      <VickiRhythm accountId={accountId} />
      <LiveVickiSection workspaceId={workspaceId} />
      <Card className="p-5">
        <VickiKnowledge accountId={accountId} compact />
      </Card>
    </div>
  );
}

/** VICKI's mode, each with what it means; only people change it (the Front Desk has the same as a picker). */
function VickiModeSection({ accountId }: { accountId: string }) {
  const settings = useQuery({ queryKey: qk.frontDeskSettings(accountId), queryFn: () => api.frontDesk.settings(accountId) });
  const save = useApiMutation((patch: Partial<FrontDeskSettings>) => api.frontDesk.setSettings(accountId, patch), {
    invalidate: [qk.frontDeskSettings(accountId)],
    success: (s) => t('VICKI works in {mode} now.', { mode: MODE_TEXT[s.mode].label }),
  });
  const mode = settings.data?.mode;
  return (
    <Section icon={<ConciergeBell />} title={t('VICKI’s mode')} description={t('How much VICKI does on her own, for the whole account.')}>
      {settings.isPending ? (
        <LoadingRows rows={2} />
      ) : settings.isError ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : (
        <div role="radiogroup" aria-label={t('VICKI’s mode')} className="grid gap-2 sm:grid-cols-2">
          {VICKI_MODES.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={m === mode}
              disabled={save.isPending}
              onClick={() => m !== mode && save.mutate({ mode: m })}
              className={cn(
                'rounded-lg border p-3 text-left text-sm transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none disabled:opacity-60',
                m === mode && 'border-primary bg-primary/5 hover:bg-primary/5',
              )}
            >
              <span className="font-medium">{MODE_TEXT[m].label}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{MODE_TEXT[m].description}</span>
            </button>
          ))}
        </div>
      )}
    </Section>
  );
}

function roleLabel(role: string): string {
  return role === 'owner' ? t('Owner') : role === 'admin' ? t('Admin') : t('Member');
}
